import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Checkbox, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import tableStyles from '../../../components/tables/table.module.scss';
import RowActionsMenu, {
  ROW_ACTIONS_COLUMN_WIDTH,
} from '../../../components/tables/RowActionsMenu';
import StatusFlag from '../../../components/elements/StatusFlag';
import {
  STATUS_TONES,
  getBooleanLabel,
  getBooleanTone,
  getNotificationStatusTone,
  getOccurrenceStatusTone,
  getTherapyStatusTone,
} from '../../../shared/utils/statusTones';
import styles from './TherapiesExpandableTable.module.scss';
import ReservationBadges from './ReservationBadges';
import {
  getHighlightedActivity,
  getOccurrenceNotificationLabel,
  getOccurrenceStatusDisplayLabel,
  getOccurrenceStatusLabel,
  shouldShowOccurrenceNotificationDate,
  sortOccurrences,
} from '../utils/therapyOccurrencePresentation';

// Interna napomena: najvise dva reda sa "..." (CSS line-clamp), ceo tekst u
// tooltip-u na hover. Tooltip i pointer kursor samo kad je tekst stvarno
// skracen. Namerno bez antd Typography ellipsis-a i bez Tooltip-a unapred: u
// 100 redova to je bio najskuplji deo renderovanja strane. Tooltip se
// montira tek na prvi hover skracene napomene.
const InternalNote = ({ note }) => {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [tooltipMounted, setTooltipMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const withTooltip = isTruncated && tooltipMounted;

  // Skrol pomera red ispod kursora bez mouseleave-a, pa tooltip ostaje
  // otvoren - zatvaramo ga na bilo koji skrol (capture hvata i skrol tabele).
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [open]);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const measure = () =>
      setIsTruncated(element.scrollHeight > element.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
    // withTooltip: omotavanje Tooltip-om (i skidanje) ponovo montira element.
  }, [note, withTooltip]);

  const text = (
    <div
      ref={ref}
      className={`${styles.internalNoteText} ${
        isTruncated ? styles.internalNoteTruncated : ''
      }`}
      onMouseEnter={
        isTruncated && !tooltipMounted
          ? () => {
              setTooltipMounted(true);
              setOpen(true);
            }
          : undefined
      }
    >
      {note || '-'}
    </div>
  );

  if (!withTooltip) return text;

  return (
    <Tooltip
      title={note}
      placement="topLeft"
      open={open}
      onOpenChange={setOpen}
      mouseEnterDelay={0.3}
    >
      {text}
    </Tooltip>
  );
};

InternalNote.propTypes = {
  note: PropTypes.string,
};

const renderSingleActivity = (
  content,
  isHighlighted = false,
  key = 'activity'
) => {
  if (!content) {
    return <div className={styles.emptyActivity}>-</div>;
  }

  return (
    <div className={styles.activityList}>
      <div
        key={key}
        className={`${styles.activityItem} ${
          isHighlighted ? styles.activityItemCurrent : ''
        }`}
      >
        {content}
      </div>
    </div>
  );
};

const renderBooleanFlag = (value) => (
  <StatusFlag tone={getBooleanTone(value)}>{getBooleanLabel(value)}</StatusFlag>
);

const renderNotificationDate = (occurrence) =>
  shouldShowOccurrenceNotificationDate(occurrence)
    ? dayjs(occurrence.notificationDate).format('DD.MM.YY')
    : '';

const renderOccurrenceNotification = ({
  occurrence,
  hasDeviceToken,
  notificationUpdatingIds,
  onNotificationChange,
}) => {
  if (
    !occurrence ||
    occurrence?.status === 'unavailable' ||
    !occurrence?.notificationStatus
  ) {
    return <span className={styles.emptyActivity}>-</span>;
  }

  const notificationDate = renderNotificationDate(occurrence);
  const notificationLabel =
    hasDeviceToken && occurrence?.notificationStatus === 'needs_contact'
      ? 'Zakazana notif.'
      : getOccurrenceNotificationLabel(occurrence);
  const showContactedAction =
    occurrence?.notificationStatus === 'needs_contact' && !hasDeviceToken;

  return (
    <div
      className={styles.notificationContent}
      title={[notificationLabel, notificationDate].filter(Boolean).join(' - ')}
    >
      <StatusFlag
        tone={getNotificationStatusTone(occurrence?.notificationStatus)}
        multiline={occurrence?.notificationStatus === 'notification_sent'}
        title={notificationLabel}
      >
        {occurrence?.notificationStatus === 'notification_sent'
          ? notificationLabel.replace(' ', '\n')
          : notificationLabel}
      </StatusFlag>
      {notificationDate ? (
        <span className={styles.notificationDate}>{notificationDate}</span>
      ) : null}
      {showContactedAction ? (
        <Checkbox
          className={styles.notificationAction}
          checked={false}
          disabled={notificationUpdatingIds.includes(occurrence._id)}
          onChange={() => onNotificationChange?.(occurrence, 'contacted')}
        >
          Javljeno
        </Checkbox>
      ) : null}
    </div>
  );
};

// Jedan red tabele. Memoizovan: promena stanja stranice (ucitavanje, pretraga,
// selekcija drugog reda) ne renderuje ponovo svih 100 redova sa antd
// komponentama - to je bio glavni uzrok sporog listanja strana.
const TherapyRow = memo(function TherapyRow({
  row,
  selectable,
  selected,
  onToggleSelection,
  actions,
  notificationUpdatingIds,
  onNotificationChange,
  handleViewUserDetails,
}) {
  // Ime pacijenta: "Prezime Ime" (fallback na userName sa beka).
  const patientName =
    [row?.lastName, row?.firstName].filter(Boolean).join(' ') ||
    row?.userName ||
    '-';
  const sortedOccurrences = sortOccurrences(row?.occurrences);
  const highlightedActivity = getHighlightedActivity(row, sortedOccurrences);
  const highlightedOccurrence = highlightedActivity?.occurrence;
  const highlightedOccurrenceStatusLabel = getOccurrenceStatusDisplayLabel(
    highlightedOccurrence
  );
  const lastOccurrence =
    sortedOccurrences.length > 0
      ? sortedOccurrences[sortedOccurrences.length - 1]
      : null;
  const highlightRenew = highlightedActivity?.type === 'renew';

  // view=table salje lastSequenceNumber umesto liste aktivnosti.
  const renewNumber =
    (row?.lastSequenceNumber ?? lastOccurrence?.sequenceNumber ?? 0) + 1 || 1;
  const renewDate = row?.renew ? dayjs(row.renew).format('DD.MM.YY') : null;
  const medicationsLabel =
    Array.isArray(row?.medications) && row.medications.length
      ? row.medications.join(', ')
      : '-';
  const medicationsLabelShort =
    Array.isArray(row?.medications) && row.medications.length
      ? row.medications.map((med) => med.split(' ')[0]).join(', ')
      : '-';
  const renewMonth = row?.renew ? (
    <span className={styles.nowrapText} style={{ textAlign: 'center' }}>
      {renewNumber}
    </span>
  ) : null;
  const renewDateContent = row?.renew ? (
    <span className={styles.nowrapText}>{renewDate || '-'}</span>
  ) : null;
  const renewStatus = row?.renew ? (
    <StatusFlag tone={STATUS_TONES.neutral} title="Obnova terapije">
      Obnova
    </StatusFlag>
  ) : null;

  return (
    <tr>
      {selectable && (
        <td className={styles.topAlignedCell}>
          <Checkbox
            checked={selected}
            onChange={(e) => onToggleSelection(row, e.target.checked)}
          />
        </td>
      )}
      <td className={styles.topAlignedCell}>
        <span
          className={styles.wrapText}
          title={patientName}
          onClick={() => handleViewUserDetails(row?.userId)}
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
        >
          {patientName}
        </span>
      </td>
      <td className={styles.topAlignedCell}>
        <span className={styles.wrapText} title={row?.dateOfBirth || '-'}>
          {/* {row?.userInternalNote || '-'} */}
          {row?.dateOfBirth
            ? dayjs(row?.dateOfBirth).format('DD.MM.YYYY')
            : '-'}
        </span>
      </td>
      <td className={styles.topAlignedCell}>
        <span className={styles.idText}>{row?.userLbo || '-'}</span>
      </td>
      <td className={styles.topAlignedCell}>
        <span className={styles.idText}>{row?.healthBookletNumber || '-'}</span>
      </td>
      <td className={styles.topAlignedCell}>
        <span className={styles.wrapText} title={row?.userPhone || '-'}>
          {row?.userPhone || '-'}
        </span>
      </td>
      <td
        className={`${styles.topAlignedCell} ${
          row?.userInternalNote?.trim()
            ? styles.internalNoteCellHighlighted
            : ''
        }`}
      >
        <InternalNote note={row?.userInternalNote} />
      </td>
      <td
        className={`${styles.topAlignedCell} ${styles.activityCell}`}
        title={row?.userPickupMethod || 'APO'}
      >
        <span className={styles.nowrapText}>
          {row?.userPickupMethod || 'APO'}
        </span>
      </td>
      <td className={`${styles.topAlignedCell} ${styles.activityCell}`}>
        <span className={styles.wrapText} title={medicationsLabel}>
          {medicationsLabelShort}
        </span>
      </td>
      <td className={`${styles.topAlignedCell} ${styles.activityCell}`}>
        {renderBooleanFlag(row?.haveApp)}
      </td>
      <td className={`${styles.topAlignedCell} ${styles.activityCell}`}>
        {renderBooleanFlag(row?.isHealthBookletVerified)}
      </td>
      <td
        colSpan={4}
        className={`${styles.topAlignedCell} ${styles.activityGroupCell}`}
      >
        {row?.isAcute && (
          <div
            style={{
              textAlign: 'center',
              fontSize: '12px',
              color: '#707374',
              fontWeight: '600',
            }}
          >
            <span>Akutna terapija</span>
          </div>
        )}
        <ReservationBadges row={row} />
        <div className={styles.activityColumns}>
          <div className={styles.activityColumn}>
            {highlightRenew
              ? renderSingleActivity(renewMonth, true, 'renew-month')
              : renderSingleActivity(
                  highlightedOccurrence ? (
                    <span
                      className={styles.nowrapText}
                      style={{ textAlign: 'center' }}
                    >
                      {highlightedOccurrence?.sequenceNumber || '-'}
                    </span>
                  ) : null,
                  !!highlightedOccurrence,
                  highlightedOccurrence?._id || 'occurrence-month'
                )}
          </div>
          <div className={styles.activityColumn}>
            {highlightRenew
              ? renderSingleActivity(renewDateContent, true, 'renew-date')
              : renderSingleActivity(
                  highlightedOccurrence ? (
                    <span className={styles.nowrapText}>
                      {highlightedOccurrence?.scheduledDate
                        ? dayjs(highlightedOccurrence.scheduledDate).format(
                            'DD.MM.YY'
                          )
                        : '-'}
                    </span>
                  ) : null,
                  !!highlightedOccurrence,
                  highlightedOccurrence?._id || 'occurrence-date'
                )}
          </div>
          <div className={styles.activityColumn}>
            {highlightRenew
              ? renderSingleActivity(renewStatus, true, 'renew-status')
              : renderSingleActivity(
                  highlightedOccurrence ? (
                    <StatusFlag
                      tone={getOccurrenceStatusTone(
                        highlightedOccurrence?.status
                      )}
                    >
                      {highlightedOccurrenceStatusLabel}
                    </StatusFlag>
                  ) : null,
                  !!highlightedOccurrence,
                  highlightedOccurrence?._id || 'occurrence-status'
                )}
          </div>
          <div className={styles.activityColumn}>
            {renderSingleActivity(
              highlightRenew
                ? null
                : renderOccurrenceNotification({
                    occurrence: highlightedOccurrence,
                    hasDeviceToken: row?.hasDeviceToken,
                    notificationUpdatingIds,
                    onNotificationChange,
                  }),
              !!highlightedOccurrence && !highlightRenew,
              highlightedOccurrence?._id || 'occurrence-notification'
            )}
          </div>
          {row?.acuteTherapies && row?.acuteTherapies?.length > 0 && (
            <div className={styles.activityFooter}>
              <span style={{ fontWeight: 'bold' }}>Akutne terapije</span>
              <div
                className={`${styles.topAlignedCell} ${styles.activityGroupCell}`}
                style={{ border: 'none' }}
              >
                <div
                  className={styles.activityColumns}
                  style={{ fontSize: '14px', color: '#4d4134' }}
                >
                  {row.acuteTherapies.map((therapy, index) => {
                    const statusLabel = getOccurrenceStatusLabel(
                      therapy?.status
                    );
                    // startDate = datum prve aktivnosti (view=table
                    // ne salje listu aktivnosti).
                    const scheduledDate = dayjs(
                      therapy?.occurrences?.[0]?.scheduledDate ||
                        therapy?.startDate
                    ).format('DD.MM.YY');
                    // Use unique keys for each div, combining therapy._id and index for uniqueness
                    return (
                      <React.Fragment key={`acute-${therapy?._id || index}`}>
                        <div
                          className={styles.activityColumn}
                          key={`empty-${therapy?._id || index}`}
                        ></div>
                        <div
                          className={styles.activityColumn}
                          key={`date-${therapy?._id || index}`}
                        >
                          <span className={styles.nowrapText}>
                            {scheduledDate}
                          </span>
                        </div>
                        <div
                          className={styles.activityColumn}
                          key={`status-${therapy?._id || index}`}
                        >
                          <StatusFlag
                            tone={getTherapyStatusTone(therapy?.status)}
                          >
                            {statusLabel}
                          </StatusFlag>
                        </div>
                        <div
                          className={styles.activityColumn}
                          key={`notification-${therapy?._id || index}`}
                        ></div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </td>
      {actions.length > 0 && (
        <td className={`${styles.topAlignedCell} table-actions-cell`}>
          <RowActionsMenu actions={actions} row={row} />
        </td>
      )}
    </tr>
  );
});

TherapyRow.propTypes = {
  row: PropTypes.object.isRequired,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  onToggleSelection: PropTypes.func,
  actions: PropTypes.array,
  notificationUpdatingIds: PropTypes.arrayOf(PropTypes.string),
  onNotificationChange: PropTypes.func,
  handleViewUserDetails: PropTypes.func,
};

const TherapiesExpandableTable = ({
  handleViewUserDetails,
  data = [],
  actions = [],
  selectable = false,
  selectedRowKeys = [],
  onSelectionChange,
  onSelectAllChange,
  selectAllLoading = false,
  notificationUpdatingIds = [],
  onNotificationChange,
  emptyState = {
    icon: '/assets/icons/no-users.svg',
    title: 'Trenutno nema terapija',
  },
  rowKey = '_id',
  tableMinWidth = null,
}) => {
  const getRowId = useCallback((row) => row?.[rowKey] || row?._id, [rowKey]);

  const selectableRows = data
    .map((row) => getRowId(row))
    .filter((id) => id !== undefined && id !== null);
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((id) => selectedRowKeys.includes(id));
  const hasSomeSelected =
    selectableRows.length > 0 &&
    selectableRows.some((id) => selectedRowKeys.includes(id));

  const handleSelectAll = (checked) => {
    if (typeof onSelectAllChange === 'function') {
      onSelectAllChange(checked, selectableRows);
      return;
    }

    if (typeof onSelectionChange !== 'function') return;

    if (checked) {
      onSelectionChange(
        [...new Set([...selectedRowKeys, ...selectableRows])],
        data
      );
      return;
    }

    onSelectionChange(
      selectedRowKeys.filter((id) => !selectableRows.includes(id)),
      data
    );
  };

  const toggleRowSelection = useCallback(
    (row, checked) => {
      const id = getRowId(row);

      if (
        id === undefined ||
        id === null ||
        typeof onSelectionChange !== 'function'
      ) {
        return;
      }

      if (checked) {
        onSelectionChange([...new Set([...selectedRowKeys, id])], [row]);
        return;
      }

      onSelectionChange(
        selectedRowKeys.filter((rowId) => rowId !== id),
        [row]
      );
    },
    [getRowId, onSelectionChange, selectedRowKeys]
  );

  return (
    <div className={styles.tableWrapper}>
      <table
        style={{ width: '100%', minWidth: tableMinWidth ?? undefined }}
        className={styles.therapiesTable}
      >
        <colgroup>
          {selectable && <col style={{ width: '3%' }} />}
          {/* Sirine su rasporedjene da se telefon i lekovi ne prelamaju
              (LBO/BZK/datum imaju fiksnu duzinu i ne trebaju vise). */}
          <col style={{ width: '13%' }} /> {/* Pacijent */}
          <col style={{ width: '8%' }} /> {/* Datum rođenja */}
          <col style={{ width: '10%' }} /> {/* LBO */}
          <col style={{ width: '9%' }} /> {/* BZK */}
          <col style={{ width: '9%' }} /> {/* Broj telefona */}
          <col style={{ width: '13%' }} /> {/* Interna napomena */}
          <col style={{ width: '8%' }} /> {/* Preuzimanje */}
          <col style={{ width: '11%' }} /> {/* Lekovi */}
          {/* Da/Ne kolone - kompaktne */}
          <col style={{ width: '5%' }} /> {/* App */}
          <col style={{ width: '5%' }} /> {/* Overena ZK */}
          {/* Mesečna aktivnost - Status dobija najviše prostora da se ne seče */}
          <col style={{ width: '5%' }} /> {/* Mesec */}
          <col style={{ width: '6%' }} /> {/* Datum */}
          <col style={{ width: '11%' }} /> {/* Status */}
          <col style={{ width: '12%' }} /> {/* Obaveštenje */}
          {/* <col style={{ width: '7%' }} /> */}
          {/* <col style={{ width: '11%' }} /> */}
          {/* <col style={{ width: '19%' }} /> */}
          {/* <col style={{ width: '6%' }} /> */}
          {/* <col style={{ width: '8%' }} /> */}
          {/* <col style={{ width: '8%' }} /> */}
          {actions.length > 0 && (
            <col style={{ width: ROW_ACTIONS_COLUMN_WIDTH }} />
          )}
        </colgroup>
        <thead>
          <tr>
            {selectable && (
              <th rowSpan={2} style={{ width: 44 }}>
                <Checkbox
                  disabled={selectAllLoading}
                  checked={allSelected}
                  indeterminate={!allSelected && hasSomeSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
            )}
            <th rowSpan={2}>Pacijent</th>
            <th rowSpan={2}>Datum rođenja</th>
            <th rowSpan={2}>LBO</th>
            <th rowSpan={2}>BZK</th>
            <th rowSpan={2}>Broj telefona</th>
            <th rowSpan={2}>Interna napomena</th>
            <th rowSpan={2}>Preuzimanje</th>
            <th rowSpan={2}>Lekovi</th>
            <th rowSpan={2}>App</th>
            <th rowSpan={2}>Overena ZK</th>
            <th colSpan={4} className={styles.groupHeader}>
              Mesečna aktivnost
            </th>
            {actions.length > 0 && (
              <th rowSpan={2} className="table-actions-cell" />
            )}
          </tr>
          <tr>
            <th className={styles.subHeader}>Mesec</th>
            <th className={styles.subHeader}>Datum</th>
            <th className={styles.subHeader}>Status</th>
            <th className={styles.subHeader}>{'Obaveštenje'}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => {
            const rowId = getRowId(row);
            return (
              <TherapyRow
                key={index}
                row={row}
                selectable={selectable}
                selected={selectable && selectedRowKeys.includes(rowId)}
                onToggleSelection={toggleRowSelection}
                actions={actions}
                notificationUpdatingIds={notificationUpdatingIds}
                onNotificationChange={onNotificationChange}
                handleViewUserDetails={handleViewUserDetails}
              />
            );
          })}
          {data.length === 0 && (
            <tr>
              <td
                colSpan={
                  14 + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0)
                }
                style={{ textAlign: 'center', padding: 8 }}
              >
                <div className={tableStyles.noData}>
                  <img src={emptyState.icon} alt="Nema podataka" />
                  <h3>{emptyState.title}</h3>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

TherapiesExpandableTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
      icon: PropTypes.node,
      className: PropTypes.string,
      danger: PropTypes.bool,
      disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      onClick: PropTypes.func.isRequired,
      visible: PropTypes.func,
    })
  ),
  selectable: PropTypes.bool,
  selectedRowKeys: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onSelectionChange: PropTypes.func,
  onSelectAllChange: PropTypes.func,
  selectAllLoading: PropTypes.bool,
  notificationUpdatingIds: PropTypes.arrayOf(PropTypes.string),
  onNotificationChange: PropTypes.func,
  emptyState: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  rowKey: PropTypes.string,
  tableMinWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  handleViewUserDetails: PropTypes.func,
};

// memo: tabela se ne renderuje ponovo dok se stranica samo ucitava ili kuca
// u pretragu (props ostaju isti).
export default memo(TherapiesExpandableTable);
